/** Public, normalized reference metadata only. Never use for API responses or user data. */
export const PUBLIC_REFERENCE_CACHE_KEY = "backtest-public-reference-v1";
export const PUBLIC_REFERENCE_MAX_ENTRIES = 48;
export const PUBLIC_REFERENCE_MAX_BYTES = 768_000;
const MAX_ENTRY_BYTES = 128_000;
const MAX_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
const KINDS = new Set(["builder-current", "builder-catalog", "market-symbols", "strategy-library"]);

export type PublicReferenceKind = "builder-current" | "builder-catalog" | "market-symbols" | "strategy-library";
export type ReferenceSnapshot<T> = { value: T; savedAt: number };
export type ReferenceStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type Entry = { key: string; savedAt: number; expiresAt: number; value: unknown };

export function publicReferenceKey(
  kind: PublicReferenceKind,
  baseURL: string,
  dimensions: readonly (string | number)[],
): string {
  const url = new URL(baseURL);
  if (!KINDS.has(kind) || !["http:", "https:"].includes(url.protocol)
    || url.username || url.password || dimensions.some((item) => String(item).length > 128)) {
    throw new Error("invalid_public_reference_key");
  }
  return JSON.stringify([kind, url.origin, ...dimensions]);
}

function browserStorage(): ReferenceStorage | undefined {
  try { return globalThis.localStorage; } catch { return undefined; }
}

function validKey(key: unknown): key is string {
  if (typeof key !== "string" || key.length > 1_000) return false;
  try {
    const parts: unknown = JSON.parse(key);
    if (!Array.isArray(parts) || parts.length < 3 || parts.length > 10
      || !KINDS.has(parts[0]) || typeof parts[1] !== "string") return false;
    const url = new URL(parts[1]);
    return ["http:", "https:"].includes(url.protocol) && url.origin === parts[1]
      && parts.slice(2).every((item) => (typeof item === "string" || typeof item === "number")
        && String(item).length <= 128);
  } catch { return false; }
}

export class PublicReferenceCache {
  private readonly storage: ReferenceStorage | undefined;
  private readonly now: () => number;

  constructor(storage = browserStorage(), now = Date.now) {
    this.storage = storage;
    this.now = now;
  }

  read<T>(key: string, parse: (value: unknown) => T): ReferenceSnapshot<T> | undefined {
    if (!validKey(key)) return undefined;
    const entry = this.entries().find((item) => item.key === key);
    if (!entry) return undefined;
    try { return { value: parse(entry.value), savedAt: entry.savedAt }; } catch {
      this.remove(key);
      return undefined;
    }
  }

  write(key: string, value: unknown, retentionMs: number): number | undefined {
    if (!validKey(key) || !Number.isFinite(retentionMs) || retentionMs <= 0) return undefined;
    const now = this.now();
    try {
      // Round-trip to detach callers' mutable references and reject unsupported values.
      const encoded = JSON.stringify(value);
      if (!encoded || encoded.length * 2 > MAX_ENTRY_BYTES) return undefined;
      const entry = {
        key, savedAt: now, expiresAt: now + Math.min(retentionMs, MAX_RETENTION_MS),
        value: JSON.parse(encoded) as unknown,
      };
      const entries = [entry, ...this.entries().filter((item) => item.key !== key)]
        .sort((a, b) => b.savedAt - a.savedAt)
        .slice(0, PUBLIC_REFERENCE_MAX_ENTRIES);
      return this.persist(entries) ? now : undefined;
    } catch { return undefined; }
  }

  remove(key: string): void {
    this.persist(this.entries().filter((item) => item.key !== key));
  }

  removeForOrigin(kind: PublicReferenceKind, baseURL: string): void {
    const origin = new URL(baseURL).origin;
    this.persist(this.entries().filter((item) => {
      const parts = JSON.parse(item.key) as unknown[];
      return parts[0] !== kind || parts[1] !== origin;
    }));
  }

  private entries(): Entry[] {
    try {
      const raw = this.storage?.getItem(PUBLIC_REFERENCE_CACHE_KEY);
      if (!raw || raw.length * 2 > PUBLIC_REFERENCE_MAX_BYTES) return [];
      const manifest = JSON.parse(raw) as { schemaVersion?: unknown; entries?: unknown };
      if (manifest?.schemaVersion !== 1 || !Array.isArray(manifest.entries)
        || manifest.entries.length > PUBLIC_REFERENCE_MAX_ENTRIES) return [];
      const now = this.now();
      const seen = new Set<string>();
      return manifest.entries.filter((candidate): candidate is Entry => {
        if (!candidate || typeof candidate !== "object") return false;
        const item = candidate as Entry;
        if (!validKey(item.key) || seen.has(item.key)
          || !Number.isFinite(item.savedAt) || !Number.isFinite(item.expiresAt)
          || item.savedAt <= 0 || item.savedAt > now + 60_000
          || item.expiresAt <= now || item.expiresAt <= item.savedAt
          || item.expiresAt - item.savedAt > MAX_RETENTION_MS
          || JSON.stringify(item.value)?.length * 2 > MAX_ENTRY_BYTES) return false;
        seen.add(item.key);
        return true;
      });
    } catch { return []; }
  }

  private persist(entries: Entry[]): boolean {
    if (!this.storage) return false;
    try {
      let encoded = JSON.stringify({ schemaVersion: 1, entries });
      while (encoded.length * 2 > PUBLIC_REFERENCE_MAX_BYTES && entries.length) {
        entries.pop();
        encoded = JSON.stringify({ schemaVersion: 1, entries });
      }
      if (entries.length) this.storage.setItem(PUBLIC_REFERENCE_CACHE_KEY, encoded);
      else this.storage.removeItem(PUBLIC_REFERENCE_CACHE_KEY);
      return true;
    } catch {
      // Storage denial/quota never blocks a live request or changes its result.
      return false;
    }
  }
}
